// Full walkthrough tutorial trigger - shows only if tutorial is not completed
import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTutorial } from './useTutorial';
import tutorialConfig from './utils/tutorialConfig.json';
import { buildSmartChartSection, waitForSmartChartAnchors } from './utils/smartChartSlides';
import { isStandaloneUser as getIsStandaloneUser } from "../../utils/standaloneUser";
import { useLauncherDock } from "../../utils/launcherDock";

const FullTutorialTrigger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStandaloneUser = getIsStandaloneUser();
  const dock = useLauncherDock();
  const {
    startTutorial,
    isActive,
    setTutorialSteps,
    exitTutorial,
    tutorialData,
    isLoading,
  } = useTutorial();

  if (isStandaloneUser) {
    return null;
  }
  
  // Don't show if tutorial is active or loading
  if (isActive || isLoading) {
    return null;
  }
  
  // Safety check: if tutorialData is not yet loaded, don't show
  if (!tutorialData || !tutorialData.onboarding) {
    return null;
  }

  // Shown even after the tutorial is completed, so users can restart the
  // training from slide 1 at any time.

  const waitForPath = async (targetPath, timeoutMs = 2000) => {
    const start = Date.now();
    while (window.location.pathname !== targetPath) {
      if (Date.now() - start > timeoutMs) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  const handleStartTutorial = async () => {
    if (isActive) {
      exitTutorial();
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Clear old steps first to ensure clean state
    setTutorialSteps([]);
    await new Promise(resolve => setTimeout(resolve, 0));

    // If the Review modal is open, run only the review-note training
    // (Review Note Page -> Save and Upload Note) right where the user is.
    const reviewModal = document.querySelector('.modal.show.modal-xl');
    if (reviewModal) {
      const reviewSection = tutorialConfig.sections.find(
        (section) => section.id === 'review_upload'
      );
      const reviewSectionSteps = reviewSection?.steps || [];
      const reviewStartIndex = reviewSectionSteps.findIndex(
        (step) => step.id === 'review_note_page'
      );
      const reviewEndIndex = reviewSectionSteps.findIndex(
        (step) => step.id === 'save_upload_note'
      );
      if (reviewStartIndex !== -1 && reviewEndIndex >= reviewStartIndex) {
        const reviewSteps = reviewSectionSteps.slice(
          reviewStartIndex,
          reviewEndIndex + 1
        );
        // This launcher is docked at the end of the modal's scroll, so the
        // note is scrolled well past the Note tab that the first slide
        // highlights. Jump back before the tooltip measures it — smooth would
        // still be moving when it does.
        reviewModal.scrollTo({ top: 0, behavior: 'auto' });
        setTutorialSteps([{ id: 'review_upload', steps: reviewSteps }]);
        await startTutorial({
          resume: false,
          startSectionId: 'review_upload',
          startStepId: reviewSteps[0].id,
          expectedSteps: reviewSteps,
          isFullTutorial: false,
        });
        return;
      }
    }

    // If the Smart Chart window is open (for ANY patient), run only the Smart
    // Chart and Smart Assist training right where the user is.
    const isSmartChartModalOpen = Boolean(
      document.querySelector('#precharting_modal_dialog')
    );
    if (isSmartChartModalOpen) {
      // The sequence starts on the Smart Chart tab; only one tabpanel is
      // mounted at a time, so switch back if the user is on Custom Prompt.
      const smartChartTab = document.querySelector('#smart-chart-tab');
      if (smartChartTab && smartChartTab.getAttribute('aria-selected') !== 'true') {
        smartChartTab.click();
      }
      // Build only once that panel and its anchors are mounted, so the tour
      // isn't missing the slides that anchor inside it.
      await waitForSmartChartAnchors();

      const smartChartSection = buildSmartChartSection({ includeSmartAssist: true });
      if (smartChartSection) {
        setTutorialSteps([smartChartSection]);
        await startTutorial({
          resume: false,
          startSectionId: smartChartSection.id,
          startStepId: smartChartSection.steps[0].id,
          expectedSteps: smartChartSection.steps,
          isFullTutorial: false,
        });
        return;
      }
    }

    // On the Settings page, run only the settings training right here
    // (dot-phrase slides; the Contact Support section is excluded).
    if (location.pathname === '/settings') {
      const settingsSection = tutorialConfig.sections.find(
        (section) => section.id === 'settings'
      );
      if (settingsSection && settingsSection.steps.length > 0) {
        setTutorialSteps([settingsSection]);
        await startTutorial({
          resume: false,
          startSectionId: 'settings',
          startStepId: settingsSection.steps[0].id,
          expectedSteps: settingsSection.steps,
          isFullTutorial: false,
        });
        return;
      }
    }

    // Otherwise start the full training from the FIRST slide (no resume),
    // from the dashboard where the tour begins.
    setTutorialSteps(tutorialConfig.sections);

    const targetPath = '/dashboard';
    if (location.pathname !== targetPath) {
      navigate(targetPath);
      await waitForPath(targetPath);
      // Give mounted page a short settle window before tooltip targeting starts.
      await new Promise(resolve => setTimeout(resolve, 120));
    }

    await startTutorial({
      resume: false,
      expectedSteps: tutorialConfig.sections.flatMap((section) => section.steps),
      isFullTutorial: true,
    });
  };
  
  const launcher = (
    <button
      type="button"
      onClick={handleStartTutorial}
      className="tutorial-launcher fixed bottom-6 z-[99999] inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent1 text-white shadow-md transition duration-200 hover:scale-110 hover:bg-purple2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent1 focus-visible:ring-offset-2"
      aria-label="Start guided tour of Knowtex"
    >
      <svg
        className="h-6 w-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" strokeWidth="2" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 16v-4m0-4h.01"
        />
      </svg>
    </button>
  );

  return dock ? createPortal(launcher, dock) : launcher;
};

export default FullTutorialTrigger;
